import React, { useEffect, useState } from 'react';
import { Table, TableBody, TableContainer, TableHead, TableRow } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import UpIcon from '@material-ui/icons/KeyboardArrowUp';
import { useIntl } from 'react-intl';
import { Redirect } from 'react-router-dom';
import LimpiarFiltros from '../../assets/images/limpiar-filtros.svg';
import { StyledTableCellProps as StyledTableCell } from '../../commons/utils/TableStyles';
import * as Utils from '../../commons/utils/Utils.js';
import InventarioMovimientosGridRow from './InventarioMovimientosGridRow.jsx';
import InventarioMovimientosGridFiltros from './InventarioMovimientosGridFiltros.jsx';
import InventarioMovimientoDetalleModal from './InventarioMovimientoDetalleModal.jsx';
import {
  buildInventarioMovimientosFilterFields,
  getInventarioMovimientosFilterDefaults,
} from './inventarioMovimientosFilterFields.js';
import { buildEmptyFilterState, serializeFilterState } from '../../commons/filters/GridFilterPanel.jsx';
import GridSearchResultsBadge from '../../commons/filters/GridSearchResultsBadge.jsx';
import GridPagination from '../../commons/grid/GridPagination.jsx';
import { GridToolbar, GridToolbarButton } from '../../commons/grid/GridToolbar.jsx';
import { GridEmptyRow, GridLoadingRow } from '../../commons/grid/GridStateRow.jsx';
import Security from '../../commons/security/Security.js';
import ConfigBusiness from '../../commons/config/ConfigBusiness.js';

/**
 * VEC-3315 Scenarios 01, 02 y 04 — historial global de movimientos de inventario.
 *
 * Es la contraparte del modal de VEC-3313: allá el historial está acotado a un ítem y un depósito y
 * se abre desde su fila; acá es la pantalla completa, con sus filtros, el responsable y el detalle
 * por movimiento. El reparto entre las dos está acordado para no construir lo mismo dos veces.
 *
 * TRES DECISIONES DE PRESENTACIÓN, TOMADAS Y CERRADAS:
 *
 *  1. GRANO LÍNEA. Un movimiento con líneas de tres ítems son tres filas. Lo que se está listando es
 *     "qué le pasó a cada ítem", no "qué documentos se emitieron".
 *  2. DOS COLUMNAS DE UBICACIÓN (Origen y Destino), no una sola "Depósito". Con una, una
 *     transferencia sería indistinguible de un ingreso.
 *  3. NUNCA SE MUESTRA SIGNO (+/−, ni colores). La dirección se lee de Tipo más Origen → Destino.
 *     Con más de un depósito filtrado el backend manda `esIngreso` en null a propósito, porque una
 *     transferencia entre los dos es egreso de uno e ingreso del otro y no hay signo correcto.
 *
 * SIN ORDENAMIENTO POR COLUMNA: el endpoint ordena siempre por fecha descendente, con desempate por
 * id de línea para que la paginación no repita ni saltee filas entre páginas. Las columnas se
 * declaran sin `onSort`, así no muestran un icono que prometa un orden que no va a pasar.
 */

const BASE_URL = '/inventario/stock/movimientos';

const COLUMN_COUNT = 10;

// El default de `GridPagination` es la constante compartida `rowsPerPage` = [10,25,50,100], que NO
// incluye el 5. Se pasa la lista propia para ofrecer las mismas opciones que el resto del módulo.
const ROWS_PER_PAGE_OPTIONS = [5, 10, 25, 50, 100];

// El endpoint topea `perPage` en 100 (ItemStockController::PER_PAGE_MAX).
const PAGINATION_INIT = { page: 0, perPage: 10 };

/** Hoy el único tipo de ubicación es el depósito del pañol. Ver el comentario en la grilla de stock. */
const UBICACION_TYPE = 'almacen';

/**
 * El query por defecto, con las MISMAS reglas de serialización que aplica el panel. Se recalcula en
 * cada llamada y no se guarda en una constante: el período por defecto es RELATIVO ("últimos 30
 * días"), así que congelarlo al cargar el módulo dejaría el rango viejo en una pestaña abierta desde
 * ayer, y la franja de resultados aparecería sola al cruzar la medianoche.
 */
const buildFilterQueryInit = (intl) => {
  const fields = buildInventarioMovimientosFilterFields();
  const estado = Object.assign(
    {},
    buildEmptyFilterState(fields),
    getInventarioMovimientosFilterDefaults(intl)
  );
  return serializeFilterState(fields, estado);
};

const InventarioMovimientosGrid = () => {
  const intl = useIntl();

  const [loading, setLoading] = useState(false);
  const [redirectTo, setRedirectTo] = useState(null);

  const [searchActive, setSearchActive] = useState(false);
  const [pagination, setPagination] = useState({ ...PAGINATION_INIT });
  const [count, setCount] = useState(0);

  const [filterResetInputs, setFilterResetInputs] = useState(false);
  const [filterQuery, setFilterQuery] = useState(() => buildFilterQueryInit(intl));

  const [dropdownActive, setDropdownActive] = useState(false);

  const [rows, setRows] = useState([]);
  const [movimientoDetalle, setMovimientoDetalle] = useState(null);

  useEffect(() => {
    // Guarda de secuencia: el `useEffect` puede dejar DOS pedidos en vuelo y sin esto pinta el que
    // responda último, no el último que se pidió. Pasa en cada mount con filtros persistidos: el
    // efecto arranca con el query por defecto (`filterQuery` se inicializa sin leer localStorage) y
    // apenas el panel se hidrata dispara `onApply`, que cambia las deps y lanza el segundo. La
    // respuesta del primero puede llegar después y sobreescribir la buena, dejando la grilla
    // mostrando datos que NO son los del filtro que muestran los controles.
    // El cleanup corre cuando cambian las deps, así que marca vencido todo pedido anterior. Cubre
    // también las carreras que no son del mount: dos clicks rápidos en Buscar, paginar y filtrar
    // encimados.
    let vigente = true;

    setLoading(true);
    if (
      Security.hasPermission('INVENTARIO_STOCK_VISUALIZAR_MOVIMIENTOS') &&
      ConfigBusiness.get('inventarioItems.habilitado') === 'true'
    ) {
      fetchRows(() => vigente);
    } else {
      setRedirectTo('/error');
    }
    return () => {
      vigente = false;
    };
  }, [pagination, filterQuery]);

  /** `sigueVigente` lo provee el efecto que disparó el pedido. Ver la guarda de secuencia. */
  const fetchRows = (sigueVigente) => {
    Utils.getData(`${BASE_URL}${getQuery()}`)
      .then((data) => {
        if (!sigueVigente()) {
          return; // llegó una respuesta vencida: la pidió un filtro que ya no es el vigente
        }
        setRows((data && data.items) || []);
        setCount((data && data.pagination && data.pagination.count) || 0);
        setLoading(false);
      })
      .catch(() => {
        if (!sigueVigente()) {
          return;
        }
        setLoading(false);
      });
  };

  const getQuery = () => {
    let query = '?page=' + (pagination.page + 1); // +1 porque la API pagina en base 1 y el front en base 0
    query += '&perPage=' + pagination.perPage;

    // La ubicación viaja COMPLETA o no viaja: con el tipo presente y los ids vacíos el backend
    // devuelve 400 (ItemStockService::normalizarUbicacion). El multiselect ya serializa "3,6".
    if (filterQuery.ubicacionId) {
      query += '&ubicacionType=' + encodeURIComponent(UBICACION_TYPE);
      query += '&ubicacionId=' + encodeURIComponent(filterQuery.ubicacionId);
    }

    query += '&tipo=' + encodeURIComponent(filterQuery.tipo || '');
    query += '&itemBusqueda=' + encodeURIComponent(filterQuery.itemBusqueda || '');

    // Las dos claves del período. Con "Todo" van vacías y el backend aplica su propia ventana de 30
    // días: no hay forma de pedir el historial completo sin fechas, y es a propósito.
    query += '&fechaDesde=' + encodeURIComponent(filterQuery.fechaDesde || '');
    query += '&fechaHasta=' + encodeURIComponent(filterQuery.fechaHasta || '');

    return query;
  };

  const handleChangePage = (event, newPage) => {
    setPagination({ ...pagination, page: newPage });
  };

  const handleChangeRowsPerPage = (event) => {
    setPagination({ ...pagination, perPage: parseInt(event.target.value, 10), page: 0 });
  };

  // LIMPIAR LA BÚSQUEDA — la "✕" de la franja de resultados: filtros al default y franja oculta.
  // "Al default" acá NO es "sin filtros": es volver a los últimos 30 días.
  const handleSearchClear = () => {
    setPagination({ ...PAGINATION_INIT });
    setFilterQuery(buildFilterQueryInit(intl));
    setSearchActive(false);
    setFilterResetInputs(true);
  };

  // RESET TOTAL — el botón "Restaurar" del toolbar. Esta grilla no tiene ordenamiento por columna ni
  // selector de columnas, así que lo único que le queda por restaurar son los filtros: reusa el
  // parcial y además cierra el panel.
  const handleFilterReset = () => {
    setDropdownActive(false);
    handleSearchClear();
  };

  /**
   * La franja se muestra solo si el query DIFIERE del default, y no con "hay algún campo cargado":
   * como acá el default ya es un filtro (los últimos 30 días), esa segunda regla la dejaría visible
   * siempre y la "✕" no podría ocultarla nunca.
   */
  const handleApplyFilters = (query) => {
    const porDefecto = buildFilterQueryInit(intl);
    const difiere = Object.keys(query).some((k) => (query[k] || '') !== (porDefecto[k] || ''));

    setPagination((prev) => ({ ...prev, page: 0 }));
    setFilterQuery(query);
    setSearchActive(difiere);
  };

  /** Encabezado no ordenable: el endpoint ordena siempre por fecha descendente. */
  const renderHeader = (messageId, label, align) => (
    <StyledTableCell align={align || 'left'}>
      {intl.formatMessage({ id: messageId, defaultMessage: label })}
    </StyledTableCell>
  );

  return (
    <>
      {redirectTo && <Redirect push to={redirectTo} />}
      <div className="row mt-2">
        <div className="col-12">
          <div className="card">
            <div className="card-content collpase show">
              <div className="card-body card-dashboard">
                <div className="container-fluid">
                  {/* Tres `col` de igual ancho: con col-6 + col + col-6 se pasa de las 12 columnas
                      de Bootstrap y el bloque de la derecha se va a la línea siguiente. */}
                  <div className="row dt-icons">
                    <div className="col">
                      {/* Sin botón "Nuevo": un movimiento no se crea desde el historial, lo genera
                          una operación (hoy, el ingreso de VEC-3314). Esta pantalla solo lee. */}
                      <div
                        className="btn btn-grey-blue box-shadow-2 btn-round btn-lg btn-dt-main round-icon"
                        onClick={() => setDropdownActive(!dropdownActive)}
                      >
                        {dropdownActive ? <UpIcon /> : <ExpandMoreIcon />}
                      </div>
                    </div>
                    <div className="col">
                      <GridSearchResultsBadge
                        visible={searchActive}
                        mostrados={rows.length}
                        total={count}
                        onReset={() => handleSearchClear()}
                      />
                    </div>
                    <div className="col">
                      <GridToolbar>
                        <GridToolbarButton
                          messageId="inventarioMovimientos.grid.tools_restore_filters.title"
                          label="Restaurar filtros"
                          onClick={() => handleFilterReset()}
                        >
                          <img id="filterReset" className="filter-reset" src={LimpiarFiltros} alt="" />
                        </GridToolbarButton>
                      </GridToolbar>
                    </div>
                  </div>
                </div>

                <InventarioMovimientosGridFiltros
                  open={dropdownActive}
                  loading={loading}
                  resetSignal={filterResetInputs}
                  onResetSignalHandled={() => setFilterResetInputs(false)}
                  onApply={handleApplyFilters}
                />

                <TableContainer>
                  <Table aria-label="inventario movimientos table" className="table-hover">
                    <TableHead>
                      <TableRow>
                        {renderHeader('inventarioMovimientos.grid.column_fecha', 'Fecha')}
                        {renderHeader('inventarioMovimientos.grid.column_tipo', 'Tipo')}
                        {renderHeader('inventarioMovimientos.grid.column_numero_de_parte', 'Nº de parte')}
                        {renderHeader('inventarioMovimientos.grid.column_item', 'Ítem')}
                        {renderHeader('inventarioMovimientos.grid.column_origen', 'Origen')}
                        {renderHeader('inventarioMovimientos.grid.column_destino', 'Destino')}
                        {renderHeader('inventarioMovimientos.grid.column_cantidad', 'Cantidad', 'right')}
                        {renderHeader('inventarioMovimientos.grid.column_costo', 'Costo unitario', 'right')}
                        {renderHeader('inventarioMovimientos.grid.column_usuario', 'Responsable')}
                        {renderHeader('inventarioMovimientos.grid.column_acciones', 'Acciones', 'center')}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <GridLoadingRow visible={loading} columns={COLUMN_COUNT} />
                      {!loading &&
                        (rows.length > 0 ? (
                          rows.map((row) => (
                            <InventarioMovimientosGridRow
                              intl={intl}
                              key={'GridRow_' + row.id}
                              row={row}
                              onVerDetalle={setMovimientoDetalle}
                            />
                          ))
                        ) : (
                          <GridEmptyRow
                            columns={COLUMN_COUNT}
                            messageId="inventarioMovimientos.grid.no_data_table"
                            label="No se encontraron movimientos en el período seleccionado."
                          />
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <GridPagination
                  rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                  count={count}
                  page={pagination.page}
                  perPage={pagination.perPage}
                  onPageChange={handleChangePage}
                  onPerPageChange={handleChangeRowsPerPage}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <InventarioMovimientoDetalleModal
        movimientoId={movimientoDetalle}
        onClose={() => setMovimientoDetalle(null)}
      />
    </>
  );
};

export default InventarioMovimientosGrid;
